import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import moment from 'moment';
import {persistenceStates} from '@computerrock/formation-core';
import {withRouter, resolveRoute} from '@computerrock/formation-router';
import {useTranslate} from '@computerrock/formation-i18n';
import {etmTaskStatusTypes} from '@ace-de/eua-entity-types';
import {useStyles, Panel, Pill, Paginator, NoResultsBlock, historyIcon, Icon, infoAlertIcon, HighlightCircle, LoaderSpinner} from '@ace-de/ui-components';
import {Table, TableHead, TableBody, TableRow, TableCell, TableCaption} from '@ace-de/ui-components/data-elements';
import * as taskActionTypes from '../../tasks/taskActionTypes';
import * as userProfileSelectors from '../../user-profiles/userProfileSelectors';
import routePaths from '../../routePaths';
import config from '../../config';

const pillVariants = {
    [etmTaskStatusTypes.DUE]: 'pending',
    [etmTaskStatusTypes.OVERDUE]: 'negative',
    [etmTaskStatusTypes.DONE]: 'positive',
};

const MyTaskPanel = props => {
    const {createTranslateShorthand, translate} = useTranslate();
    const {cx} = useStyles();
    const translatePanel = createTranslateShorthand('my_task_panel');
    const {initiateTaskEdit, currentUsersTasks, history, currentUsersTasksCount} = props;
    const {taskSearchPersistenceState, isTaskSearchError} = props;
    const queryParams = new URLSearchParams(history.location.search);
    const paginatorCount = Math.ceil(currentUsersTasksCount / config.TASKS_DEFAULT_PAGE_SIZE);
    const isSearchInProgress = taskSearchPersistenceState === persistenceStates.PENDING;

    const handlePaginationPage = page => {
        const apiQueryParams = new URLSearchParams(queryParams);
        apiQueryParams.set('page', `${page}`);
        apiQueryParams.set('size', `${config.TASKS_DEFAULT_PAGE_SIZE}`);

        const queryParamsString = apiQueryParams ? apiQueryParams.toString() : '';
        history.push(resolveRoute(routePaths.DASHBOARD, {}, {search: queryParamsString}));
    };

    return (
        <Panel
            title={translatePanel('panel_title.my_tasks', {amountOfTasks: currentUsersTasksCount})}
            className={cx('ace-c-panel--full-bleed-content')}
        >
            <Table qaIdent="task-list-results">
                {!isSearchInProgress && (!currentUsersTasks || currentUsersTasks.length === 0 || isTaskSearchError) && (
                    <TableCaption>
                        <NoResultsBlock
                            colorVariant={!isTaskSearchError ? 'default' : 'negative'}
                            icon={(
                                <Icon
                                    className={cx('ace-c-icon--xxl', {
                                        'global!ace-c-icon--color-warning': isTaskSearchError,
                                    })}
                                    icon={isTaskSearchError ? infoAlertIcon : historyIcon}
                                />
                            )}
                            message={isTaskSearchError
                                ? translatePanel('results_error.description')
                                : translatePanel('no_results.message')}
                            description={isTaskSearchError
                                ? translatePanel('results_error.message')
                                : translatePanel('no_results.description')}
                        />
                    </TableCaption>
                )}
                {isSearchInProgress && (
                    <TableCaption>
                        <HighlightCircle
                            className={cx([
                                'ace-c-highlight-circle--medium',
                                'ace-c-highlight-circle--primary-highlight',
                                'global!ace-u-margin--top-64',
                                'global!ace-u-margin--bottom-64',
                            ])}
                        >
                            <LoaderSpinner />
                        </HighlightCircle>
                        <p
                            className={cx([
                                'global!ace-u-margin--top-16',
                                'global!ace-u-margin--bottom-64',
                                'global!ace-u-typography--variant-h4',
                            ])}
                        >
                            {translatePanel('loader_message.search_in_progress')}
                        </p>
                    </TableCaption>
                )}
                <TableHead>
                    <TableRow>
                        <TableCell
                            className={cx('ace-u-padding--left-32')}
                            qaIdentPart="task-due-date-time"
                            colSpan={3}
                        >
                            {translatePanel('table_header.due_date_time')}
                        </TableCell>
                        <TableCell qaIdentPart="sc-case-id" colSpan={2}>
                            {translatePanel('table_header.service_case_id')}
                        </TableCell>
                        <TableCell qaIdentPart="task-description" colSpan={5}>
                            {translatePanel('table_header.description')}
                        </TableCell>
                        <TableCell qaIdentPart="task-type" colSpan={4}>
                            {translatePanel('table_header.type')}
                        </TableCell>
                        <TableCell qaIdentPart="task-status" colSpan={3}>
                            {translatePanel('table_header.status')}
                        </TableCell>
                        <TableCell
                            className={cx('ace-u-padding--right-32')}
                            qaIdentPart="task-created-by"
                            colSpan={3}
                        >
                            {translatePanel('table_header.created_by')}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {!isSearchInProgress && !isTaskSearchError
                        && currentUsersTasks?.length > 0 && currentUsersTasks.map(task => (
                            <TableRow
                                key={task.id}
                                qaIdentPart={task.id}
                                onClick={() => initiateTaskEdit({taskId: task.id})}
                            >
                                <TableCell
                                    className={cx('ace-u-padding--left-32')}
                                    qaIdentPart="task-due-date-time"
                                    qaIdentPartPostfix={task.id}
                                    colSpan={3}
                                >
                                    {moment(task.dueDate).format('DD.MM.YYYY - HH:mm')}
                                </TableCell>
                                <TableCell
                                    qaIdentPart="sc-case-id"
                                    qaIdentPartPostfix={task.id}
                                    colSpan={2}
                                >
                                    {task.assignedCaseId}
                                </TableCell>
                                <TableCell
                                    qaIdentPart="task-description"
                                    qaIdentPartPostfix={task.id}
                                    colSpan={5}
                                >
                                    {task.description}
                                </TableCell>
                                <TableCell
                                    qaIdentPart="task-type"
                                    qaIdentPartPostfix={task.id}
                                    colSpan={4}
                                >
                                    {translate(`global.task_category.${task.category.toLowerCase()}`)}
                                </TableCell>
                                <TableCell
                                    qaIdentPart="task-status"
                                    qaIdentPartPostfix={task.id}
                                    colSpan={3}
                                >
                                    <Pill type={pillVariants[task.status]}>
                                        {translate(`global.task_status.${task.status.toLowerCase()}`)}
                                    </Pill>
                                </TableCell>
                                <TableCell
                                    className={cx('ace-u-padding--right-32')}
                                    qaIdentPart="task-created-by"
                                    qaIdentPartPostfix={task.id}
                                    colSpan={3}
                                >
                                    {task.createdByUser || '-'}
                                </TableCell>
                            </TableRow>
                    ))}
                </TableBody>
            </Table>
            {currentUsersTasks.length > 0 && (
                <Paginator
                    page={+queryParams.get('page')}
                    count={paginatorCount}
                    onClick={page => handlePaginationPage(page)}
                />
            )}
        </Panel>
    );
};

MyTaskPanel.propTypes = {
    initiateTaskEdit: PropTypes.func.isRequired,
    currentUsersTasks: PropTypes.array,
    currentUsersTasksCount: PropTypes.number,
    history: PropTypes.object,
    taskSearchPersistenceState: PropTypes.string.isRequired,
    isTaskSearchError: PropTypes.bool,
};

MyTaskPanel.defaultProps = {
    currentUsersTasks: [],
    currentUsersTasksCount: 0,
    history: null,
    isTaskSearchError: false,
};

const mapStateToProps = state => {
    return {
        users: userProfileSelectors.getUsers(state),
        currentUsersTasks: state.tasks.taskSearchResults,
        currentUsersTasksCount: state.tasks.taskSearchResultsCount,
        taskSearchPersistenceState: state.tasks.taskSearchPersistenceState,
        isTaskSearchError: state.tasks.isTaskSearchError,
    };
};

const mapDispatchToProps = dispatch => ({
    initiateTaskEdit: payload => dispatch({
        type: taskActionTypes.INITIATE_TASK_EDIT_FLOW,
        payload,
    }),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(MyTaskPanel));
